import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@codes/ui-kit/components/ui/avatar';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Card } from '@codes/ui-kit/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Image } from '@codes/ui-kit/components/ui/image';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { AgentView } from '@lemoncloud/ssocio-chatbots-api';
import { EllipsisVertical, Loader2, MessageSquare } from 'lucide-react';

import { useDeleteAgent } from '@codes/agents';
import { AiLogoDarkImage, AiLogoImage } from '@codes/assets';
import { useWebCoreStore } from '@codes/web-core';

export interface AgentCardProps {
    agent: AgentView;
    onChat: (id: string) => void;
    onSettings: (id: string) => void;
    onShare: (id: string) => void;
    onDelete: (id: string) => void;
}

export const AgentCard: React.FC<AgentCardProps> = ({ agent, onChat, onSettings, onShare, onDelete }) => {
    const { t } = useTranslation();
    const userId = useWebCoreStore(state => state.userId);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const deleteAgentMutation = useDeleteAgent();

    const agentName = agent.meta$?.nick ?? agent.id ?? t('agent.listItem.states.noName');
    const avatarUrl = typeof (agent.meta$ as any)?.avatarUrl === 'string' ? (agent.meta$ as any).avatarUrl : '';
    const isDeleting = deleteAgentMutation.isPending;

    const handleDelete = async () => {
        try {
            await deleteAgentMutation.mutateAsync(agent.id ?? '');
            toast({
                title: t('agent.listItem.messages.deleteSuccess.title'),
                description: t('agent.listItem.messages.deleteSuccess.description', { agentName }),
            });
            onDelete(agent.id ?? '');
        } catch (error) {
            toast({
                variant: 'destructive',
                title: t('agent.listItem.messages.deleteError.title'),
                description: t('agent.listItem.messages.deleteError.description'),
            });
        } finally {
            setShowDeleteDialog(false);
        }
    };

    return (
        <>
            <Card className="h-[272px] flex flex-col border border-border dark:border-border relative">
                {isDeleting && (
                    <div className="absolute inset-0 bg-card/80 dark:bg-card/80 rounded-lg flex items-center justify-center z-10 backdrop-blur-sm">
                        <div className="text-center">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                        </div>
                    </div>
                )}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Avatar className="bg-card dark:bg-background">
                            {avatarUrl ? <AvatarImage src={avatarUrl} alt={agentName ?? ''} /> : null}
                            <AvatarFallback className="bg-transparent">
                                <Image className="w-5 h-5" src={AiLogoImage} darkSrc={AiLogoDarkImage} />
                            </AvatarFallback>
                        </Avatar>
                        <div className="text-lg font-semibold">{agentName}</div>
                    </div>
                    {userId === agent.$?.uid && (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                className="group"
                                onClick={e => e.stopPropagation()}
                                disabled={isDeleting}
                            >
                                <EllipsisVertical className="w-4 h-4 text-sub group-hover:text-icon-hover" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[100px] rounded-lg px-3 py-[2px]">
                                <DropdownMenuItem
                                    className="text-xs text-error focus:text-error"
                                    onClick={e => {
                                        e.stopPropagation();
                                        setShowDeleteDialog(true);
                                    }}
                                    disabled={isDeleting}
                                >
                                    {t('agent.listItem.actions.deleteMenuItem')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
                <div className="text-sub text-sm mt-2 text-ellipsis break-words line-clamp-2">
                    {agent.meta$?.role || t('agent.listItem.states.noRole')}
                </div>
                <div className="space-y-[13px] mt-[10px]">
                    <div className="flex items-center justify-between text-sm">
                        <div className="font-medium">{t('agent.catalog.fields.model')}</div>
                        <div className="text-sub">{agent.brain$?.name || t('agent.listItem.states.notAvailable')}</div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                        <div className="font-medium">{t('agent.catalog.fields.creator')}</div>
                        <div className="text-sub">{agent.$?.uid}</div>
                    </div>
                </div>
                <div className="mt-auto flex items-center gap-2">
                    <Button
                        size="md"
                        className="w-[162px] h-[38px] flex items-center gap-2 transition-all duration-200 hover:scale-105"
                        onClick={() => onChat(agent.id ?? '')}
                        disabled={isDeleting}
                    >
                        <MessageSquare className="w-4 h-4" />
                        {t('agent.catalog.actions.chat')}
                    </Button>
                    <div className="flex-1 flex items-center gap-2">
                        <Button
                            size="md"
                            variant="outline"
                            className="h-[38px] flex-1 transition-all duration-200 hover:scale-105"
                            onClick={() => onSettings(agent.id ?? '')}
                            disabled={isDeleting}
                        >
                            {t('agent.catalog.actions.settings')}
                        </Button>
                        <Button
                            size="md"
                            variant="outline"
                            className="h-[38px] flex-1 border-point text-point transition-all duration-200 hover:scale-105"
                            onClick={() => onShare(agent.id ?? '')}
                            disabled={isDeleting}
                        >
                            {t('agent.catalog.actions.share')}
                        </Button>
                    </div>
                </div>
            </Card>
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('agent.listItem.dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t('agent.listItem.dialog.description', { agentName }),
                                }}
                            />
                            <br />
                            {t('agent.listItem.dialog.warning')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>
                            {t('agent.listItem.dialog.actions.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction className="text-error" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                t('agent.listItem.dialog.actions.delete')
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
